import HomeService from '@baserow/modules/core/services/home'

/**
 * Origem Flow: tabelas favoritadas pela estrela do breadcrumb (estilo
 * ClickUp). Persistido no backend (/api/home/favorites/) para sincronizar
 * entre dispositivos. Favoritos antigos do localStorage são migrados uma
 * única vez no primeiro load e a chave local é removida.
 */
const LEGACY_STORAGE_KEY = 'origemFlowStarredTables'

function readLegacyStorage() {
  try {
    const ids = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || '[]')
    return Array.isArray(ids) ? ids : []
  } catch (error) {
    return []
  }
}

function writeLegacyStorage(ids) {
  try {
    if (ids.length === 0) {
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    } else {
      localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(ids))
    }
  } catch (error) {
    // Sem localStorage não há nada a persistir.
  }
}

export const state = () => ({
  loaded: false,
  loading: false,
  tableIds: [],
})

export const mutations = {
  SET_LOADING(state, value) {
    state.loading = value
  },
  SET_TABLE_IDS(state, ids) {
    state.tableIds = ids
    state.loaded = true
  },
}

export const actions = {
  async load({ state, commit, dispatch }, { force = false } = {}) {
    if (state.loading || (state.loaded && !force)) {
      return
    }
    commit('SET_LOADING', true)
    try {
      const { data } = await HomeService(this.$client).fetchFavoriteTables()
      let ids = data.table_ids || []

      // Migração única: favoritos que só existiam neste navegador sobem
      // para o backend. Erros permanentes (4xx: tabela apagada/sem acesso)
      // descartam o id; erros transitórios (rede/5xx) mantêm o id no
      // localStorage para tentar de novo no próximo load.
      const legacyIds = readLegacyStorage().filter((id) => !ids.includes(id))
      const remaining = []
      for (const tableId of legacyIds) {
        try {
          const { data: toggled } = await HomeService(
            this.$client
          ).toggleFavoriteTable(tableId)
          ids = toggled.table_ids || ids
        } catch (error) {
          const status = error.response?.status
          if (!status || status >= 500) {
            remaining.push(tableId)
          }
        }
      }
      writeLegacyStorage(remaining)

      commit('SET_TABLE_IDS', ids)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async toggleTable({ state, commit }, tableId) {
    // Atualização otimista com rollback em caso de erro.
    const previous = state.tableIds
    const optimistic = previous.includes(tableId)
      ? previous.filter((id) => id !== tableId)
      : [...previous, tableId]
    commit('SET_TABLE_IDS', optimistic)
    try {
      const { data } = await HomeService(this.$client).toggleFavoriteTable(
        tableId
      )
      commit('SET_TABLE_IDS', data.table_ids || optimistic)
    } catch (error) {
      commit('SET_TABLE_IDS', previous)
      throw error
    }
  },
}

export const getters = {
  isTableStarred: (state) => (tableId) => state.tableIds.includes(tableId),
  getTableIds(state) {
    return state.tableIds
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
